import { useCallback, useEffect, useState } from "react";
import type { AgentMessage } from "@/api/observability";
import { fetchAgentInbox } from "@/api/observability";
import { formatRelativeTime } from "@/pages/observabilityUtils";

const MSG_TYPE_STYLES: Record<string, string> = {
  delegation: "bg-violet-500/15 text-violet-300 border-violet-400/30",
  notification: "bg-sky-500/15 text-sky-300 border-sky-400/30",
  request: "bg-amber-500/15 text-amber-300 border-amber-400/30",
  result: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
  intent: "bg-rose-500/15 text-rose-300 border-rose-400/30",
};

function extractMessageHint(payload: Record<string, unknown> | null | undefined): string | null {
  if (!payload) return null;
  const str = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : null);
  return str(
    payload.task ?? payload.intent ?? payload.summary ?? payload.description ??
    payload.message ?? payload.content ?? payload.action,
  );
}

type AgentInboxPanelProps = {
  agentName: string;
};

export function AgentInboxPanel({ agentName }: AgentInboxPanelProps) {
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!agentName) return;
    setLoading(true);
    setError(null);
    fetchAgentInbox(agentName)
      .then(setMessages)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false));
  }, [agentName]);

  useEffect(() => {
    load();
  }, [load]);

  if (!agentName) {
    return <p className="text-sm text-muted-foreground">Select an agent to view inbox.</p>;
  }

  return (
    <div className="rounded-lg border border-border bg-background/40 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-primary">Agent Inbox</h4>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {error ? (
        <div className="space-y-2">
          <p className="text-xs text-destructive">{error}</p>
          <button type="button" onClick={load} className="text-xs text-primary hover:underline">
            Retry
          </button>
        </div>
      ) : messages.length === 0 && !loading ? (
        <p className="text-sm text-muted-foreground">No pending messages.</p>
      ) : (
        <ul className="space-y-2">
          {messages.map((message) => {
            const hint = extractMessageHint(message.payload as Record<string, unknown> | null | undefined);
            const typeStyle = MSG_TYPE_STYLES[message.type] ?? "bg-muted/50 text-muted-foreground border-border/40";
            return (
              <li key={message.id} className="rounded-lg border border-border bg-background/60 p-3 text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-semibold text-primary">{message.sender}</span>
                  <span
                    className="shrink-0 text-muted-foreground"
                    title={new Date(message.created_at || "").toLocaleString()}
                  >
                    {formatRelativeTime(message.created_at)}
                  </span>
                </div>
                <span className={`mt-2 inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${typeStyle}`}>
                  {message.type}
                </span>
                {hint ? (
                  <p className="mt-2 line-clamp-2 text-foreground/70">{hint}</p>
                ) : (
                  <p className="mt-2 italic text-muted-foreground/60">No summary available.</p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default AgentInboxPanel;
